import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import LoadingFallback from '../components/common/LoadingFallback'
import ProtectedRoute from './ProtectedRoute'
import { usePermission } from '../hooks/usePermissions' 

// Lazy pages 
const LoginPage = lazy(() => import('../pages/auth/LoginPage'))
const SignupPage = lazy(() => import('../pages/auth/SignupPage'))
const ClientsPage = lazy(() => import('../pages/clients/ClientsPage'))
const EntreprisesPage = lazy(() => import('../pages/entreprises/EntreprisesPage'))
const FacturesPage = lazy(() => import('../pages/factures/FacturesPage'))
const TasksPage = lazy(() => import('../pages/tasks/TasksPage'))
const UserSettingsPage = lazy(() => import('../pages/settings/UserSettingsPage'))
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'))
const KanbanPage = lazy(() => import('../pages/tasks/KanbanPage'))
const EditTaskPage = lazy(() => import('../pages/tasks/edit_task_page'))
const CreateTaskPage = lazy(() => import('../pages/tasks/create_task_page'))

// Pages Utilisateurs & Rôles
const UsersPage = lazy(() => import('../pages/users/UsersPage'))
const RoleGroupsPage = lazy(() => import('../pages/users/RoleGroupsPage')) 

const FormManagementPage = lazy(() => import('../pages/forms/FormManagementPage'))
const WorkflowTypesPage = lazy(() => import('../pages/tasks/WorkflowTypesPage'))
const WorkflowListPage = lazy(() => import('../pages/tasks/WorkflowListPage'))

// Pages du module Employés (RH)
const EmployeesPage = lazy(() => import('../pages/employees/EmployeesPage'))
const DepartmentsPage = lazy(() => import('../pages/employees/departments/DepartmentsPage'))
const JobTitlesPage = lazy(() => import('../pages/employees/jobs/JobTitlesPage'))
const PolicyProfilesPage = lazy(() => import('../pages/employees/policies/PolicyProfilesPage'))
const EmploymentContractsPage = lazy(() => import('../pages/employees/contracts/EmploymentContractsPage'))

// --- FEUILLES DE TEMPS ---
const TimesheetListPage = lazy(() => import('../pages/employees/timesheets/TimesheetListPage'))
const TimesheetDetailPage = lazy(() => import('../pages/employees/timesheets/TimesheetDetailPage'))
const TimesheetCreatePage = lazy(() => import('../pages/employees/timesheets/TimesheetCreatePage'))

const MyTimesheetsPage = lazy(() => import('../pages/employees/timesheets/MyTimesheetsPage'))
const PlanningCalendarPage = lazy(() => import('../pages/employees/planning/PlanningCalendarPage'))

// --- CONGÉS ---
const MyLeavesPage = lazy(() => import('../pages/employees/leaves/MyLeavesPage'))
const AdminLeaveRequestsPage = lazy(() => import('../pages/employees/leaves/AdminLeaveRequestsPage'))

// --- ACCUEIL EMPLOYÉ ---
const EmployeeHomePage = lazy(() => import('../pages/employees/EmployeeHomePage'));

// --- SÉCURITÉ / GESTION DES MOTS DE PASSE (ADMIN) ---
const PasswordsAdminPage = lazy(() => import('../pages/security/passwords'))
const PasswordSharesPage = lazy(() => import('../pages/security/PasswordSharesPage'))
const AccessTypesPage = lazy(() => import('../pages/security/AccessTypesPage'))
const PasswordCategoriesPage = lazy(() => import('../pages/security/PasswordCategoriesPage'))
const AuditLogsPage = lazy(() => import('../pages/security/audit-logs'))

// --- SÉCURITÉ / ESPACE EMPLOYÉ 
const EmployeePasswordsPage = lazy(() => import('../pages/security/EmployeePasswordsPage'))
const EmployeeSharedPasswordsPage = lazy(() => import('../pages/security/EmployeeSharedPasswordsPage'))

const AppRoutes = () => {
  const { hasPermission, isLoading } = usePermission();

  if (isLoading) {
    return <LoadingFallback message="Vérification des autorisations..." />;
  }

  const canViewClients = hasPermission('can_view_client');
  const canViewInvoices = hasPermission('can_view_invoice');
  
  const canViewTasks = hasPermission('can_view_task');
  const canCreateTasks = hasPermission('can_create_task');
  const canEditTasks = hasPermission('can_edit_task');

  const canManageRoles = hasPermission('can_manage_roles');    
  const canManageUsers = hasPermission('can_manage_users');    
  const canViewEmployees = hasPermission('can_view_employee'); 

  const canViewTimesheets = hasPermission('can_view_timesheet');
  const canApproveTimesheets = hasPermission('can_manage_timesheets');
  
  const canViewPlanning = hasPermission('can_view_planning');
  const canManageLeaves = hasPermission('can_manage_leaves');

  // Permission pour la gestion RH avancée (Profils de politiques & Contrats)
  const canManageAdminRH = canManageRoles || canManageUsers;
  
  // Détermine si l'utilisateur a accès au Tableau de bord (Admin / Manager)
  const canViewDashboard = canViewEmployees || canManageUsers;
  // Si l'utilisateur n'a pas accès au dashboard admin, on le redirige vers /accueil
  const defaultHomeRoute = canViewDashboard ? "/dashboard" : "/accueil";
  
  return (
    <Suspense fallback={<LoadingFallback message="Chargement..." />}>
      <Routes>

        {/* ROUTES PUBLIQUES */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        {/* REDIRECTION RACINE DYNAMIQUE */}
        <Route path="/" element={<Navigate to={defaultHomeRoute} replace />} />

        {/* TABLEAU DE BORD (Sécurisé) */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              {canViewDashboard ? <DashboardPage /> : <Navigate to="/accueil" replace />}
            </ProtectedRoute>
          } 
        />
        
        {/* ACCUEIL EMPLOYÉ */}
        <Route 
          path="/accueil" 
          element={
            <ProtectedRoute>
              <EmployeeHomePage />
            </ProtectedRoute>
          } 
        />

        <Route path="/settings" element={<ProtectedRoute><UserSettingsPage /></ProtectedRoute>} />
        <Route path="/entreprises" element={<ProtectedRoute><EntreprisesPage /></ProtectedRoute>} />

        {/* MODULE CLIENTS */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              {canViewClients ? <ClientsPage /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        />

        {/* MODULE FACTURES */}
        <Route
          path="/factures"
          element={
            <ProtectedRoute>
              {canViewInvoices ? <FacturesPage /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        />

        {/* MODULE TÂCHES */}
        <Route
          path="/taches"
          element={
            <ProtectedRoute>
              {canViewTasks ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route index element={<TasksPage />} />
          <Route path="creer" element={canCreateTasks ? <CreateTaskPage /> : <Navigate to="/taches" replace />} />
          <Route path=":id/edit" element={canEditTasks ? <EditTaskPage /> : <Navigate to="/taches" replace />} />
        </Route>

        {/* MODULE KANBAN */}
        <Route
          path="/kanban"
          element={
            <ProtectedRoute>
              {canViewTasks ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route index element={<KanbanPage />} />
          <Route path=":workflowId" element={<KanbanPage />} />
        </Route>

        {/* MODULE FEUILLES DE TEMPS (TIMESHEETS) */}
        <Route
          path="/timesheets"
          element={
            <ProtectedRoute>
              {canViewTimesheets ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route 
            index 
            element={
              canApproveTimesheets ? <TimesheetListPage /> : <MyTimesheetsPage />
            } 
          />

          <Route path="create" element={<TimesheetCreatePage />} />
          
          <Route path=":id" element={<TimesheetDetailPage />} />
        </Route>

        {/* MODULE CALENDRIER (PLANNING)*/}
        <Route 
          path="/planning" 
          element={ 
            <ProtectedRoute> 
              {canViewPlanning ? <PlanningCalendarPage /> : <Navigate to={defaultHomeRoute} replace />} 
            </ProtectedRoute>  
          }
        />

        {/* MODULE CONGÉS (EMPLOYÉ & MANAGER) */}
        <Route 
          path="/my-leaves" 
          element={
            <ProtectedRoute>
              <MyLeavesPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/admin/leave-requests" 
          element={
            <ProtectedRoute>
              {canManageLeaves || canViewEmployees ? <AdminLeaveRequestsPage /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          } 
        />

        {/* CONFIGURATION */}
        <Route 
          path="/config/formulaires" 
          element={
            <ProtectedRoute>
              {canManageRoles ? <FormManagementPage /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/config/kanbans" 
          element={
            <ProtectedRoute>
              {canManageRoles ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="types" replace />} />
          <Route path="types" element={<WorkflowTypesPage />} />
          <Route path="liste" element={<WorkflowListPage />} />
        </Route>

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              {canManageUsers ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route index element={<UsersPage />} />
          <Route path="roles" element={<RoleGroupsPage />} />
        </Route>

        <Route
          path="/admin/employees"
          element={
            <ProtectedRoute>
              {canManageAdminRH ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route index element={<EmployeesPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="job-titles" element={<JobTitlesPage />} />
          <Route path="policy-profiles" element={<PolicyProfilesPage />} />
          <Route path="employment-contracts" element={<EmploymentContractsPage />} />
        </Route>
        {/* MODULE SÉCURITÉ & ACCÈS (ADMIN) */}
        <Route
          path="/admin/security"
          element={
            <ProtectedRoute>
              {canManageUsers || canManageRoles ? <Outlet /> : <Navigate to={defaultHomeRoute} replace />}
            </ProtectedRoute>
          }
        >
          <Route path="passwords" element={<PasswordsAdminPage />} />
          <Route path="password-shares" element={<PasswordSharesPage />} />
          <Route path="types" element={<AccessTypesPage />} />
          <Route path="categories" element={<PasswordCategoriesPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
        </Route>

        <Route path="/mes-passwords" element={ <ProtectedRoute><EmployeePasswordsPage /></ProtectedRoute> }/>
        <Route path="/mes-passwords/partages" element={<ProtectedRoute><EmployeeSharedPasswordsPage /></ProtectedRoute>}/>


        <Route path="*" element={<Navigate to={defaultHomeRoute} replace />} />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes